import React, { FunctionComponent } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Candidate, ModelVariant } from '../../api';
import { Anchor, Question } from '../../App.styles';
import { Button, NavFooter } from '../../components';
import InfoBox from '../../components/InfoBox/InfoBox';
import Result from '../../components/Result/Result';
import {
  Disclaimer,
  ModelBrand,
  ResultInfo,
} from '../../components/Result/Result.styles';
import RingtercomCards from '../../components/RingtercomCards';
import {
  CandidateModelsContext,
  FlowStep,
  GlobalContext,
  StepperContext,
  updateFlowStep,
} from '../../contexts';
import { PRIMARY_500 } from '../../styles/colors';
import { StepKey } from '../../types';
import {
  closeAmzModal,
  formatCurrency,
  iconUrl,
  imagesUrl,
  isAmazonHost,
  RingtercomFeature,
  RingtercomVersion,
  TestId,
} from '../../utils';
import { environment } from '../../utils/environment';
import { Treatment } from '../../weblab/treatment';
import { Icon, ICON_TYPES } from './Success.styles';

const SuccessVideoSupport: FunctionComponent = () => {
  const { dispatch } = React.useContext(GlobalContext);
  const { data } = React.useContext(CandidateModelsContext);
  const { stepperData, updateStepperData, steps } =
    React.useContext(StepperContext);
  const {
    brand,
    model,
    variant: stepperVariant,
    secondAttempt,
    candidate,
  } = stepperData;
  const { t } = useTranslation('common');
  const { coupon, treatment } = environment;
  const candidates = data?.candidates;

  const supportedHwVersions = new Set<RingtercomVersion>();
  const variant = stepperVariant || model?.variants[0];
  let hasVideo: boolean | undefined = undefined;
  const mapToHwVersion = (candidate: ModelVariant | Candidate) => {
    hasVideo =
      hasVideo ||
      (candidate.hw_version === RingtercomVersion.V2 && candidate.has_video);
    return candidate.hw_version;
  };

  if (candidate) {
    const modelName = model?.model_name;
    candidates
      ?.filter((c) => {
        const labels = candidate?.terminal_labels?.join('');

        return (
          c.support === 'full_support' &&
          (modelName ? c.model_name === modelName : true) &&
          labels === c.terminal_labels.join('')
        );
      })
      .map(mapToHwVersion)
      .forEach((v) => {
        if (v) {
          supportedHwVersions.add(v);
        }
      });
  } else if (model) {
    model?.variants
      .filter(
        (v) =>
          v.support === 'full_support' &&
          v.backplate_number === variant?.backplate_number
      )
      .map(mapToHwVersion)
      .forEach((v) => {
        if (v) {
          supportedHwVersions.add(v);
        }
      });
  } else {
    const supportByHedge = data?.support_by_hw_version?.[RingtercomVersion.V2];
    const supportByForge =
      data?.support_by_hw_version?.[RingtercomVersion.V1_5];

    hasVideo = supportByHedge?.[RingtercomFeature.VIDEO] === 'full_support';
    const hasHedgeAudio =
      supportByHedge?.[RingtercomFeature.AUDIO] === 'full_support';
    const hasForgeAudio =
      supportByForge?.[RingtercomFeature.AUDIO] === 'full_support';

    if (hasVideo || (hasHedgeAudio && !hasForgeAudio)) {
      supportedHwVersions.add(RingtercomVersion.V2);
    }
    if (hasForgeAudio) {
      supportedHwVersions.add(RingtercomVersion.V1_5);
    }
  }

  const videoCardVisibility =
    hasVideo ||
    (supportedHwVersions.size === 1 &&
      supportedHwVersions.has(RingtercomVersion.V2));

  const audioCardVisibility = Array.from(supportedHwVersions).some(
    (hw) => hw !== RingtercomVersion.V2
  );

  const ringtercomCardsData = [
    {
      tag: t('compatibilityOptions.tag'),
      videoSupported: true,
      visible: videoCardVisibility,
      deviceName: t('results.success.ringtercomCard.titleVideo'),
      deviceDescription: t('results.success.ringtercomCard.videoDescription'),
      deviceIcon: iconUrl.personDetectionOutline,
      features: [
        <>
          <img alt="" src={iconUrl.supportVideo}></img>
          <span>
            <Trans
              i18nKey="common:results.success.ringtercomCard.remoteVideoFeature"
              components={{
                bold: <span className="fw-bold" />,
              }}
            />
          </span>
        </>,
        <>
          <img alt="" src={iconUrl.amazonKeyAccess}></img>
          <span>{t('results.success.ringtercomCard.autoVerifyFeature')}</span>
        </>,
        <>
          <img alt="" src={iconUrl.sharedAlerts}></img>
          <span>{t('results.success.ringtercomCard.shareKeysFeature')}</span>
        </>,
      ],
      shopUrl: t(
        `results.success.ringtercomCard.shopNowBtnUrl.${
          isAmazonHost() ? 'amazon' : 'ring'
        }.video`
      ),
    },
    {
      tag: t('results.success.ringtercomCard.noVideoTag'),
      videoSupported: false,
      visible: audioCardVisibility,
      deviceName: t('results.success.ringtercomCard.titleAudio'),
      deviceDescription: t('results.success.ringtercomCard.audioDescription'),
      deviceIcon: iconUrl.audioIntercom,
      features: [
        <>
          <img alt="" src={iconUrl.generalAudio}></img>
          <span>{t('results.success.ringtercomCard.remoteAudioFeature')}</span>
        </>,
        <>
          <img alt="" src={iconUrl.amazonKeyAccess}></img>
          <span>{t('results.success.ringtercomCard.autoVerifyFeature')}</span>
        </>,
        <>
          <img alt="" src={iconUrl.sharedAlerts}></img>
          <span>{t('results.success.ringtercomCard.shareKeysFeature')}</span>
        </>,
      ],
      shopUrl: t(
        `results.success.ringtercomCard.shopNowBtnUrl.${
          isAmazonHost() ? 'amazon' : 'ring'
        }.audio`
      ),
    },
  ]
    .filter((card) => card.visible)
    .sort((x, y) =>
      x.videoSupported === y.videoSupported ? 0 : x.videoSupported ? -1 : 1
    );

  return (
    <Result
      image={{
        source: hasVideo ? imagesUrl.success_video : imagesUrl.success_audio,
        altText: t('images.success'),
        cssClasses: 'w-100 h-100 object-fit-cover',
      }}
      stepKey={StepKey.Success}
    >
      <div className="row h-100" data-testid={TestId.Success}>
        <div className="flex-grow-1 column">
          <div>
            {audioCardVisibility && !videoCardVisibility ? (
              <>
                <Question className="my-2">
                  {t('results.success.titleCompatibilityResults')}
                </Question>
                <Question className="d-flex">
                  <Icon type={ICON_TYPES.SUCCESS} className="me-3" />
                  <span>{t('results.success.titleForgeCompatible')}</span>
                </Question>
                <Question className="mb-3 d-flex">
                  <Icon type={ICON_TYPES.NEGATIVE} className="me-3" />
                  <span>{t('results.success.titleHedgeNotCompatible')}</span>
                </Question>

                <ResultInfo>
                  <span>
                    <Trans
                      i18nKey="common:results.success.descriptionForgeOnly"
                      values={{
                        brandModel:
                          brand && model?.model_name
                            ? `${brand}, ${model.model_name}`
                            : '',
                      }}
                      components={{
                        brandModelStyle: <span className="fw-bold" />,
                      }}
                    />
                  </span>
                </ResultInfo>

                <ResultInfo>
                  <span>
                    {t('results.success.videoDisclaimerHedgeForgeOnly')}
                  </span>
                </ResultInfo>
              </>
            ) : (
              <>
                <Question className="my-2">
                  {t('results.success.title')}
                </Question>
                <ResultInfo>
                  <span>{t('results.success.description')}</span>
                  {brand && model ? (
                    <span>
                      :
                      <ModelBrand>
                        {brand}, {model.model_name}.
                      </ModelBrand>
                    </span>
                  ) : (
                    <span>.</span>
                  )}
                </ResultInfo>

                <ResultInfo>
                  {videoCardVisibility && !hasVideo && !audioCardVisibility && (
                    <span>{t('results.success.videoDisclaimerHedge')}</span>
                  )}

                  {!videoCardVisibility && audioCardVisibility && (
                    <span>
                      {t(
                        treatment === Treatment.V3_5
                          ? 'results.success.videoDisclaimer-v3_5'
                          : 'results.success.videoDisclaimer'
                      )}
                    </span>
                  )}
                </ResultInfo>

                {ringtercomCardsData.length > 1 && (
                  <ResultInfo className={`${!isAmazonHost() && 'fw-semibold'}`}>
                    <span>{t('results.success.pickModel')}</span>
                  </ResultInfo>
                )}
              </>
            )}

            {variant?.ring_to_open && (
              <InfoBox
                title={t('results.success.r2oTitle')}
                description={t('results.success.r2oDescription')}
              />
            )}

            {treatment === Treatment.V3_5 && !secondAttempt && (
              <ResultInfo className="mx-0 fw-semibold">
                {t('results.success.multiDoor')}{' '}
                <Anchor
                  tabIndex={0}
                  role="button"
                  onClick={() => {
                    updateStepperData({
                      activeStep: steps.findIndex(
                        (s) => s === StepKey.IsParallelAfterSuccess
                      ),
                      multiDoor: true,
                      secondAttempt: true,
                    });
                    dispatch(
                      updateFlowStep({
                        flowStep: FlowStep.compatibilityChecker,
                      })
                    );
                  }}
                >
                  {t('buttons.continue')}
                </Anchor>
              </ResultInfo>
            )}
          </div>

          <div className="col gy-4 border-top mt-3">
            <RingtercomCards cards={ringtercomCardsData} />
            {coupon?.code && coupon?.amount && coupon?.currency && (
              <div className="border rounded-2 p-4 mt-4 mb-4 flex-grow-1 col justify-content-start">
                <div className="mb-3">
                  <Question>
                    <Trans
                      i18nKey="common:results.success.coupon.congrats"
                      values={{
                        code: coupon.code,
                        amount: formatCurrency(coupon.amount, coupon.currency),
                      }}
                      components={{
                        couponCode: <span style={{ color: PRIMARY_500 }} />,
                        amount: <span style={{ color: PRIMARY_500 }} />,
                      }}
                    />
                  </Question>
                </div>

                <div>
                  <Disclaimer>
                    <Trans
                      i18nKey="common:results.success.coupon.disclaimer"
                      values={{
                        code: coupon.code,
                      }}
                      components={{
                        tncLink: (
                          <Anchor
                            tabIndex={0}
                            target="_blank"
                            href="/gp/help/customer/display.html?nodeId=G8LF2MC32ZASDWVU"
                            role="button"
                          />
                        ),
                      }}
                    />
                  </Disclaimer>
                </div>
              </div>
            )}
          </div>
        </div>

        {isAmazonHost() && (
          <NavFooter>
            <Button type="button" onClick={closeAmzModal}>
              {t('header.close')}
            </Button>
            <Button
              type="button"
              ghost
              onClick={() => {
                dispatch(
                  updateFlowStep({ flowStep: FlowStep.compatibilityChecker })
                );
              }}
            >
              {t('header.back')}
            </Button>
          </NavFooter>
        )}
      </div>
    </Result>
  );
};

export default SuccessVideoSupport;
